import { create } from 'zustand'
import type { EncounterRecord, EncounterType } from '@/entities/encounter/types'
import { useOutboxStore } from '@/features/offline/model/outboxStore'
import { createId } from '@/shared/lib/id'
import { getErrorMessage, isLikelyOfflineError } from '@/shared/lib/network'
import {
  encountersRepository,
  type RepoEncounterRecord,
} from '@/shared/repositories/encounters/encountersRepository'

interface CreateEncounterInput {
  patientId: string
  type: EncounterType
  occurredAt: string
  complaint?: string | null
  description: string
  principioAtivo?: string | null
  concentracao?: string | null
  unidade?: string | null
  descricaoLivre?: string | null
}

interface EncounterStoreState {
  encountersByPatientId: Record<string, EncounterRecord[]>
  loadingByPatientId: Record<string, boolean>
  fetchEncountersByPatient: (patientId: string) => Promise<void>
  addEncounterAsync: (input: CreateEncounterInput) => Promise<EncounterRecord>
  getEncountersByPatient: (patientId: string) => EncounterRecord[]
}

const sortByOccurredAtDesc = (encounters: EncounterRecord[]): EncounterRecord[] =>
  [...encounters].sort(
    (left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
  )

const mapRepoToEntity = (record: RepoEncounterRecord): EncounterRecord => ({
  id: record.id,
  patientId: record.patientId,
  createdByUserId: record.createdByUserId,
  type: record.type,
  occurredAt: record.occurredAt,
  complaint: record.complaint,
  description: record.description,
  createdAt: record.createdAt,
  principioAtivo: record.principioAtivo,
  concentracao: record.concentracao,
  unidade: record.unidade,
  descricaoLivre: record.descricaoLivre,
})

export const useEncounterStore = create<EncounterStoreState>()((set, get) => ({
  encountersByPatientId: {},
  loadingByPatientId: {},

  fetchEncountersByPatient: async (patientId) => {
    if (!patientId) return

    set((state) => ({
      loadingByPatientId: {
        ...state.loadingByPatientId,
        [patientId]: true,
      },
    }))

    try {
      const { encounters } = await encountersRepository.listByPatient(patientId)
      const mapped = sortByOccurredAtDesc(encounters.map(mapRepoToEntity))
      set((state) => ({
        encountersByPatientId: {
          ...state.encountersByPatientId,
          [patientId]: mapped,
        },
      }))
    } finally {
      set((state) => ({
        loadingByPatientId: {
          ...state.loadingByPatientId,
          [patientId]: false,
        },
      }))
    }
  },

  addEncounterAsync: async (input) => {
    const payload = {
      patientId: input.patientId,
      type: input.type,
      occurredAt: input.occurredAt,
      complaint: input.complaint?.trim() || null,
      description: input.description.trim(),
      principioAtivo: input.principioAtivo?.trim() || null,
      concentracao: input.concentracao?.trim() || null,
      unidade: input.unidade?.trim() || null,
      descricaoLivre: input.descricaoLivre?.trim() || null,
    }

    try {
      const { encounter } = await encountersRepository.create(payload)

      const mapped = mapRepoToEntity(encounter)
      set((state) => {
        const existing = state.encountersByPatientId[input.patientId] ?? []
        return {
          encountersByPatientId: {
            ...state.encountersByPatientId,
            [input.patientId]: sortByOccurredAtDesc([mapped, ...existing]),
          },
        }
      })

      return mapped
    } catch (error) {
      if (!isLikelyOfflineError(error)) {
        throw error
      }

      const now = new Date().toISOString()
      const parsedOccurredAt = new Date(input.occurredAt)
      const normalizedOccurredAt = Number.isNaN(parsedOccurredAt.getTime())
        ? now
        : parsedOccurredAt.toISOString()

      const optimisticRecord: EncounterRecord = {
        id: createId(),
        patientId: input.patientId,
        createdByUserId: 'pending',
        type: input.type,
        occurredAt: normalizedOccurredAt,
        complaint: payload.complaint,
        description: payload.description,
        createdAt: now,
        principioAtivo: payload.principioAtivo,
        concentracao: payload.concentracao,
        unidade: payload.unidade,
        descricaoLivre: payload.descricaoLivre,
      }

      set((state) => {
        const existing = state.encountersByPatientId[input.patientId] ?? []
        return {
          encountersByPatientId: {
            ...state.encountersByPatientId,
            [input.patientId]: sortByOccurredAtDesc([optimisticRecord, ...existing]),
          },
        }
      })

      useOutboxStore.getState().enqueue(
        'encounter.create',
        payload,
        getErrorMessage(error, 'Falha de conexão ao sincronizar atendimento.'),
      )

      return optimisticRecord
    }
  },

  getEncountersByPatient: (patientId) => get().encountersByPatientId[patientId] ?? [],
}))

